import { SetURLSearchParams } from 'react-router-dom'
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

import { useMasterBillStore } from './useMasterBillStore'

import { customsV2Service } from '@/services'
import { ApiResponse } from '@/services/api'
import { MasterBillSchema } from '@/services/CustomsV2Service'

interface SearchState {
  loading: boolean
  masterBills: MasterBillSchema[]
  masterBillId?: number
}

interface SearchActions {
  // 恢复url参数
  restore: (search: URLSearchParams) => void
  // 当前MBL的ID
  setMasterBillId: (masterBillId: number) => void
  // MBL列表
  setMasterBills: (masterBills: MasterBillSchema[]) => void
  // 获取MBL列表
  fetchMasterBills: () => Promise<ApiResponse>
  // 改变MBL
  changeMasterBillId: (
    id?: number,
    searchParams?: URLSearchParams,
    setSearchParams?: SetURLSearchParams
  ) => void
  // 清除选择MBL
  clearFilter: () => void
  // initialize: (masterBillId: number) => void
}

type SearchStore = SearchState & SearchActions

const initialState: SearchState = {
  loading: false,
  masterBills: []
}

export const useSearchStore = create<SearchStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setMasterBillId: (value) => set({ masterBillId: value }),
        setMasterBills: (bills) => set({ masterBills: bills }),

        // 获取MBL列表
        fetchMasterBills: async () => {
          try {
            set({ loading: true })
            const { success, message, data } =
              await customsV2Service.getMasterBills()
            if (success && data) {
              get().setMasterBills(data)
            }
            return { success, message }
          } finally {
            set({ loading: false })
          }
        },

        // 从 URL 参数或 localStorage 恢复选中的主提单
        restore: (searchParams) => {
          // 优先从URL参数获取
          const urlParam = searchParams.get('masterBill')
          if (urlParam) {
            const urlValue = parseInt(urlParam, 10)
            if (!isNaN(urlValue)) {
              get().setMasterBillId(urlValue)
            }
          }
        },

        // 复合动作：选择主提单并持久化到 URL
        changeMasterBillId: (id, searchParams, setSearchParams) => {
          // 在所有操作之前，先清除旧的主提单数据
          useMasterBillStore.getState().clearMasterBill()
          try {
            // 更新 URL 参数（如果提供了参数）
            if (searchParams && setSearchParams) {
              const newSearchParams = new URLSearchParams(searchParams)
              if (id !== undefined) {
                newSearchParams.set('masterBill', id.toString())
              } else {
                newSearchParams.delete('masterBill')
              }
              setSearchParams(newSearchParams, { replace: true })
            }
            // 更新状态
            set({ masterBillId: id })
          } catch (error) {
            console.error('更新url参数失败:', error)
            // 即使url参数持久化失败，也要更新状态
            set({ masterBillId: id })
          } finally {
            // 获取MBL数据
            if (id) {
              useMasterBillStore.getState().fetchMasterBill(id)
            }
          }
        },

        clearFilter: () => {
          set({ masterBillId: undefined })
          // 清除masterBill已经加载的数据
          useMasterBillStore.getState().clearMasterBill()
        }
      }),
      {
        name: 'customs-search-storage'
      }
    )
  )
)

export const useSearchState = () => {
  const masterBillId = useSearchStore((state) => state.masterBillId)
  const masterBills = useSearchStore((state) => state.masterBills)
  return { masterBillId, masterBills }
}

export const useSearchActions = () => {
  const restore = useSearchStore((state) => state.restore)
  const fetchMasterBills = useSearchStore((state) => state.fetchMasterBills)
  const changeMasterBillId = useSearchStore((state) => state.changeMasterBillId)
  const clearFilter = useSearchStore((state) => state.clearFilter)
  return { restore, fetchMasterBills, changeMasterBillId, clearFilter }
}
